import React from 'react';
import { Property } from '../../domain/entities/Property';
import {
  formatInt,
  formatPrice,
  formatPricePerSqft,
  getMarketTimeText,
} from '../../../../shared/utils/formatters/propertyFormatters';
import { useHoverStore } from '../../../public-map/application/hoverStore';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import FavoriteToggle from '../../../../components/common/FavoriteToggle';

export type TableViewTableProps = {
  properties: Property[];
  activeId?: string | number;
  onRowClick?: (item: Property) => void;
  className?: string;
};

export function TableViewTablePR({
  properties,
  activeId,
  onRowClick,
  className,
}: TableViewTableProps) {
  const {
    setHoveredPropertyId,
    isPropertyHovered,
    isPropertyInHoveredCluster,
  } = useHoverStore();
  const headerClasses =
    'px-1 py-1 uppercase text-[10px] font-semibold bg-gray-100';

  // Body templates for custom column rendering
  const addressBodyTemplate = (property: Property) => (
    <span className="max-w-[100px] overflow-hidden truncate whitespace-nowrap block text-[10px]">
      {property.address ?? '—'}
    </span>
  );

  const priceBodyTemplate = (property: Property) => (
    <span className="tabular-nums">{formatPrice(property.listPrice)}</span>
  );

  const pricePerSqftBodyTemplate = (property: Property) => (
    <span className="tabular-nums">
      {formatPricePerSqft(property.listPricePerSqft)}
    </span>
  );

  const sqftBodyTemplate = (property: Property) => (
    <span className="tabular-nums">{formatInt(property.sqft)}</span>
  );

  const bedsBodyTemplate = (property: Property) => (
    <span className="tabular-nums">{formatInt(property.beds)}</span>
  );

  const bathsBodyTemplate = (property: Property) => (
    <span className="tabular-nums">{formatInt(property.baths)}</span>
  );

  const equityBodyTemplate = (property: Property) => (
    <span className="tabular-nums">{formatPrice(property.equity)}</span>
  );

  const onMarketBodyTemplate = (property: Property) => (
    <span className="tabular-nums">
      {getMarketTimeText(
        property.status,
        property.daysOnMarket,
        property.daysSinceSold
      )}
    </span>
  );

  const favoriteBodyTemplate = (property: Property) => (
    <span>
      <FavoriteToggle propertyId={property.id} />
    </span>
  );

  const addToListBodyTemplate = (property: Property) => (
    <input
      type="checkbox"
      onChange={() => console.log('addToListChange', property.id)}
      onClick={(e) => e.stopPropagation()}
    />
  );

  const rowClassName = (property: Property) => {
    const isSingleHover =
      isPropertyHovered(property.id) &&
      !isPropertyInHoveredCluster(property.id);
    const isInCluster = isPropertyInHoveredCluster(property.id);

    // Selection state (activeId) - background color
    const activeClass = property.id === activeId ? 'bg-indigo-50' : '';

    // Hover state - ring outline (separate from selection)
    const hoverClass = isSingleHover
      ? 'ring-2 ring-inset ring-indigo-500'
      : isInCluster
      ? 'ring-1 ring-inset ring-indigo-300'
      : '';

    return `${activeClass} ${hoverClass}`;
  };

  return (
    <div
      id="table-view-wrapper"
      className={`bg-white overflow-hidden ${className || ''}`}
    >
      <style>{`
        .table-view-table-compact .p-sortable-column-icon {
          width: 0.625rem !important;
          height: 0.625rem !important;
          font-size: 0.625rem !important;
          margin-left: 0.125rem !important;
        }
      `}</style>
      <div
        id="table-view-table-container"
        className="h-full min-h-0 overflow-y-auto overflow-x-auto"
      >
        <DataTable
          id="table-view-table"
          value={properties}
          tableStyle={{ minWidth: '100%' }}
          className="text-[10px] table-view-table-compact"
          rowClassName={rowClassName}
          onRowClick={(e) => onRowClick?.(e.data as Property)}
          selectionMode="single"
          pt={{
            root: { className: 'min-h-0' },
            wrapper: { className: 'min-h-0' },
            bodyRow: {
              className:
                'cursor-pointer odd:bg-white even:bg-gray-50 hover:bg-gray-100 transition-all duration-150',
              onMouseEnter: (e: React.MouseEvent<HTMLTableRowElement>) => {
                const rowElement = e.currentTarget;
                const rowIndex = rowElement.rowIndex - 1; // -1 because header is row 0
                if (rowIndex >= 0 && rowIndex < properties.length) {
                  setHoveredPropertyId(properties[rowIndex].id);
                }
              },
              onMouseLeave: () => setHoveredPropertyId(null),
            },
          }}
        >
          <Column
            field="address"
            header="Address"
            sortable
            headerClassName={headerClasses}
            bodyClassName="px-1 py-2"
            style={{ width: '20%' }}
            body={addressBodyTemplate}
          />
          <Column
            field="listPrice"
            header="Price"
            sortable
            headerClassName={headerClasses}
            bodyClassName="px-1 py-2"
            style={{ width: '10%' }}
            body={priceBodyTemplate}
          />
          <Column
            field="listPricePerSqft"
            header="$/Sqft"
            sortable
            headerClassName={headerClasses}
            bodyClassName="px-1 py-2"
            style={{ width: '8%' }}
            body={pricePerSqftBodyTemplate}
          />
          <Column
            field="sqft"
            header="Sqft"
            sortable
            headerClassName={headerClasses}
            bodyClassName="px-1 py-2"
            style={{ width: '7%' }}
            body={sqftBodyTemplate}
          />
          <Column
            field="beds"
            header="Beds"
            sortable
            headerClassName={headerClasses}
            bodyClassName="px-1 py-2"
            style={{ width: '6%' }}
            body={bedsBodyTemplate}
          />
          <Column
            field="baths"
            header="Baths"
            sortable
            headerClassName={headerClasses}
            bodyClassName="px-1 py-2"
            style={{ width: '6%' }}
            body={bathsBodyTemplate}
          />
          <Column
            field="equity"
            header="Pot. Equity"
            sortable
            headerClassName={headerClasses}
            bodyClassName="px-1 py-2"
            style={{ width: '10%' }}
            body={equityBodyTemplate}
          />
          <Column
            field="daysOnMarket"
            header="On Market"
            sortable
            headerClassName={headerClasses}
            bodyClassName="px-1 py-2"
            style={{ width: '13%' }}
            body={onMarketBodyTemplate}
          />
          <Column
            header="Fav"
            headerClassName={headerClasses}
            bodyClassName="px-1 py-2"
            style={{ width: '6%' }}
            body={(property) => favoriteBodyTemplate(property)}
          />
          {/* <Column
            header="Add"
            headerClassName={headerClasses}
            bodyClassName="px-1 py-2"
            style={{ width: '6%' }}
            body={addToListBodyTemplate}
          /> */}
        </DataTable>
      </div>
    </div>
  );
}

// Export PrimeReact version as the default TableViewTable
export const TableViewTable = TableViewTablePR;
